/**
 * Fotografia clínica do momento: consolida exposições ativas (doses registradas,
 * residuais e prescrições de manutenção não confirmadas) em eixos clínicos,
 * ponderando pela exposição estimada AGORA — uma cauda pequena não gera alerta grande.
 * Intensidades são relativas ao modelo; não são concentração plasmática.
 */
import type { SessionContextSnapshot, SessionSubstanceState, ContextInteraction } from "@/domain/sessionContextEngine";

export type ExposureTier = "dominante" | "ativa" | "baixa" | "residual";
export type ExposureSource = "registrada" | "estimada-manutencao";
export type InteractionKind =
  | "farmacocinetica" | "farmacodinamica" | "aditiva" | "sobreposicao"
  | "carry-over" | "baixa-relevancia" | "clinicamente-importante";

export interface MomentExposure {
  key: string;
  name: string;
  source: ExposureSource;
  phase: string;
  intensity: number; // 0–100 relativo
  tier: ExposureTier;
  note?: string;
}

export interface MomentAxis {
  id: string;
  label: string;
  physiology: string;
  score: number; // 0–100 agregado ponderado
  level: "baixo" | "moderado" | "marcado";
  main: { name: string; share: number; residualOnly: boolean }[];
  changesInterpretation: boolean;
  objective?: string;
}

export interface PrescriptionInput {
  name: string;
  dose?: string | null;
  usualTime?: string | null;
  isMaintenance?: boolean;
}

export interface VitalInput { systolic?: number | null; diastolic?: number | null; heartRate?: number | null; measuredAt?: string }

export interface ClinicalMoment {
  exposures: MomentExposure[];
  axes: MomentAxis[];
  dominant?: MomentAxis;
  interactions: (ContextInteraction & { kind: InteractionKind; magnitude: number })[];
  lowRelevance: (ContextInteraction & { kind: InteractionKind; magnitude: number })[];
  modifiers: string[];
  uncertain: string[];
  wouldChange: string[];
}

const AXES: { id: string; label: string; physiology: string; match: RegExp }[] = [
  { id: "simpatico", label: "Ativação simpática/catecolaminérgica", physiology: "PA, FC, ansiedade, insônia, tremor", match: /estimul|anfet|lisdex|metilfen|cocain|cafein|modafin|bupropi|venlafax|desvenlafax|dulox|atomox|efedr|nicotin|mdma/ },
  { id: "depressor", label: "Depressão do SNC / sedação", physiology: "sedação, ataxia, amnésia, depressão respiratória", match: /benzo|clonaz|diazep|alpraz|lorazep|midaz|zolpid|alcool|etanol|opio|oxicod|morfin|codein|tramadol|metadon|fentan|gaba|pregab|gabapent|quetiap|mirtaz|trazod|olanz|clonidin|antihistam|prometaz|hidroxizin|ghb/ },
  { id: "serotonergico", label: "Carga serotoninérgica", physiology: "náusea, agitação, tremor, hiperreflexia (toxicidade se alta)", match: /isrs|irsn|serot|sertral|escitalo|citalo|fluox|parox|fluvox|vortiox|venlafax|desvenlafax|dulox|tramadol|mdma|trazod|mirtaz|lsd|psiloc/ },
  { id: "hipotensor", label: "Efeito hipotensor/bradicardizante", physiology: "PA baixa, bradicardia, tontura ortostática", match: /clonidin|guanfac|prazos|propranol|metoprol|quetiap|mirtaz|trazod|pramipex/ },
  { id: "dopaminergico", label: "Tônus dopaminérgico/recompensa", physiology: "motivação, impulsividade, craving, redose", match: /anfet|lisdex|metilfen|cocain|pramipex|bupropi|levodop|ropinir|mdma|nicotin/ },
];

function text(state: SessionSubstanceState) {
  return `${state.name} ${state.key} ${state.categories.join(" ")}`.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

export function tierFor(intensity: number): ExposureTier {
  if (intensity >= 60) return "dominante";
  if (intensity >= 25) return "ativa";
  if (intensity >= 8) return "baixa";
  return "residual";
}

function level(score: number): MomentAxis["level"] {
  return score >= 55 ? "marcado" : score >= 25 ? "moderado" : "baixo";
}

const MAINTENANCE_DEFAULT_INTENSITY = 45; // steady-state presumido, não confirmado

export function buildClinicalMoment(input: {
  snapshot: SessionContextSnapshot;
  prescriptions?: PrescriptionInput[];
  vitals?: VitalInput[];
  session?: { sleepHours?: number | null; caffeineMg?: number | null; alcohol?: boolean | null };
}): ClinicalMoment {
  const { snapshot } = input;
  const registeredKeys = new Set(snapshot.states.map((s) => s.key));
  const pseudoStates: SessionSubstanceState[] = [...snapshot.states];
  const exposures: MomentExposure[] = snapshot.states.map((s) => ({
    key: s.key, name: s.name, source: "registrada", phase: String(s.phase),
    intensity: Math.round(s.effect0to100), tier: tierFor(s.effect0to100),
    note: s.carryOver ? "inclui carry-over de doses anteriores" : undefined,
  }));

  for (const p of input.prescriptions ?? []) {
    if (!p.isMaintenance) continue;
    const key = p.name.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();
    if ([...registeredKeys].some((k) => key.includes(k) || k.includes(key))) continue;
    exposures.push({
      key, name: p.name, source: "estimada-manutencao", phase: "steady-state presumido",
      intensity: MAINTENANCE_DEFAULT_INTENSITY, tier: "ativa",
      note: `Prescrição ativa${p.dose ? ` (${p.dose}${p.usualTime ? ` · ${p.usualTime}` : ""})` : ""}; tomada de hoje não registrada — adesão não confirmada.`,
    });
    pseudoStates.push({ key, name: p.name, effect0to100: MAINTENANCE_DEFAULT_INTENSITY, phase: "steady-state" as any, categories: [], tolerance: { key, label: "não estimável", confidence: "baixa", reason: "" }, carryOver: false });
  }

  const axes: MomentAxis[] = [];
  for (const axis of AXES) {
    const contributors = pseudoStates
      .filter((s) => axis.match.test(text(s)))
      .map((s) => ({ name: s.name, w: Math.max(0, s.effect0to100) }))
      .filter((c) => c.w > 0)
      .sort((a, b) => b.w - a.w);
    if (!contributors.length) continue;
    // soma saturante: o maior contribui inteiro, os demais com peso decrescente
    const score = Math.min(100, contributors.reduce((acc, c, i) => acc + c.w * (i === 0 ? 1 : 0.5 / i), 0));
    const total = contributors.reduce((a, c) => a + c.w, 0) || 1;
    const main = contributors.slice(0, 4).map((c) => ({ name: c.name, share: Math.round((c.w / total) * 100), residualOnly: c.w < 8 }));
    let objective: string | undefined;
    const lastVital = (input.vitals ?? []).filter((v) => v.systolic || v.heartRate).sort((a, b) => String(b.measuredAt).localeCompare(String(a.measuredAt)))[0];
    if (lastVital && (axis.id === "simpatico" || axis.id === "hipotensor")) {
      objective = `Última medida: ${lastVital.systolic ?? "?"}/${lastVital.diastolic ?? "?"} mmHg · FC ${lastVital.heartRate ?? "?"}`;
    }
    axes.push({ id: axis.id, label: axis.label, physiology: axis.physiology, score: Math.round(score), level: level(score), main, changesInterpretation: score >= 25, objective });
  }
  axes.sort((a, b) => b.score - a.score);

  // Interações: magnitude = menor intensidade entre os envolvidos (a interação exige ambos presentes)
  const intensityOf = (name: string) => {
    const n = name.toLowerCase();
    const e = exposures.find((x) => x.name.toLowerCase() === n || n.includes(x.key) || x.key.includes(n));
    return e?.intensity ?? 0;
  };
  const classified = snapshot.interactions.map((it) => {
    const mags = it.involved.map(intensityOf).filter((v) => v > 0);
    const magnitude = mags.length ? Math.min(...mags) : 0;
    const blob = `${it.title} ${it.mechanism}`.toLowerCase();
    let kind: InteractionKind =
      /cyp|metabol|inibi|induc|farmacocin/.test(blob) ? "farmacocinetica"
      : /soma|adit|depress|sedac|serotonin/.test(blob) ? "aditiva"
      : "farmacodinamica";
    if (magnitude < 8) kind = "carry-over";
    else if (magnitude < 20 && it.level !== "alto" && it.level !== "emergencia") kind = "baixa-relevancia";
    else if (it.level === "alto" || it.level === "emergencia" || (it.level === "relevante" && magnitude >= 40)) kind = "clinicamente-importante";
    return { ...it, kind, magnitude };
  });
  const interactions = classified.filter((i) => i.kind !== "carry-over" && i.kind !== "baixa-relevancia").sort((a, b) => b.magnitude - a.magnitude);
  const lowRelevance = classified.filter((i) => i.kind === "carry-over" || i.kind === "baixa-relevancia");

  const modifiers: string[] = [];
  if (input.session?.sleepHours != null && input.session.sleepHours < 5) modifiers.push(`Sono curto (${input.session.sleepHours} h) amplifica ativação, irritabilidade e prejuízo cognitivo.`);
  if ((input.session?.caffeineMg ?? 0) > 0) modifiers.push(`Cafeína ${input.session!.caffeineMg} mg soma ao eixo simpático.`);
  if (input.session?.alcohol) modifiers.push("Álcool no dia soma ao eixo depressor e reduz julgamento de dose.");

  const uncertain: string[] = [];
  const estimated = exposures.filter((e) => e.source === "estimada-manutencao");
  if (estimated.length) uncertain.push(`Exposição presumida (não registrada hoje): ${estimated.map((e) => e.name).join(", ")}.`);
  if (snapshot.states.some((s) => !s.body)) uncertain.push("Algumas substâncias sem PK estruturada — fase e intensidade com baixa confiança.");
  if (!(input.vitals ?? []).length) uncertain.push("Sem PA/FC recente: eixo autonômico apenas modelado, sem sinal objetivo.");

  const wouldChange: string[] = [];
  if (estimated.length) wouldChange.push("Confirmar horário real das tomadas de manutenção.");
  if (axes.some((a) => a.id === "simpatico" && a.level !== "baixo") && !(input.vitals ?? []).length) wouldChange.push("Medir PA e FC agora.");
  if (input.session?.sleepHours == null) wouldChange.push("Registrar horas de sono da última noite.");

  return { exposures: exposures.sort((a, b) => b.intensity - a.intensity), axes, dominant: axes[0], interactions, lowRelevance, modifiers, uncertain, wouldChange };
}
